import { useQuery } from '@tanstack/react-query';
import { apiFetch } from '@/lib/api/client';

/** Item de taxonomia Nível 1 (motivo de descarte, tese, setor, fundo, tipo de originadora). */
export interface ReferenceLookup {
  id: number;
  name: string;
}

/** Usuário ativo para selects de líder/equipe/originador interno. */
export interface ReferenceUser {
  id: string;
  name: string;
  email: string;
  role: string;
}

/** Resposta do GET /api/reference — lookups dos formulários numa única chamada. */
export interface ReferenceData {
  discardReasons: ReferenceLookup[];
  theses: ReferenceLookup[];
  sectors: ReferenceLookup[];
  funds: ReferenceLookup[];
  originatorTypes: ReferenceLookup[];
  /** Usuários selecionáveis em Equipe e Originador interno — SEM o sentinela "Não atribuído". */
  users: ReferenceUser[];
  /** Usuários para o campo Líder — inclui o sentinela rotulado "TBD" no topo (DEC-035 revisado). */
  leadUsers: ReferenceUser[];
  /** RI (ADR-0013, Bloco 2 / Fatia A) — taxonomias novas do agregado Investor. */
  investorTypes: ReferenceLookup[];
  coinvestmentTypes: ReferenceLookup[];
  decisionStructures: ReferenceLookup[];
}

export const REFERENCE_QUERY_KEY = ['reference'] as const;

/**
 * Usuário sentinela "Não atribuído" (seedado via SQL, líder dos deals migrados sem responsável —
 * DEC-035). É um usuário ATIVO (o validador exige líder ativo), então a API o devolve em /reference.
 * Por decisão de Henrique (lote 1, item 2), ele passou a ser selecionável **como "TBD" no campo
 * Líder** (cria/edita) — inverte o follow-up original do DEC-035 ("excluir do picker"). Continua
 * filtrado de Equipe e Originador interno, onde "TBD" não faz sentido. Use `leadUsers` no campo
 * Líder e `users` nos demais.
 */
export const UNASSIGNED_USER_ID = '00000000-0000-0000-0000-000000000002';

/** Rótulo do sentinela quando oferecido como líder não atribuído. */
export const TBD_LEAD_LABEL = 'TBD (não atribuído)';

/**
 * Dados de referência (lookups) para os formulários do produto: motivos de descarte, teses,
 * setores, fundos, tipos de originadora e usuários ativos. Cacheado com staleTime alto —
 * taxonomias mudam raramente (E8 admin). Alimenta o modal de descarte e o formulário de criar deal.
 * Expõe duas listas de usuários: `users` (sem o sentinela, para Equipe/Originador interno) e
 * `leadUsers` (com o sentinela rotulado "TBD" no topo, para o campo Líder).
 */
export function useReferenceData() {
  return useQuery({
    queryKey: REFERENCE_QUERY_KEY,
    queryFn: () => apiFetch<ReferenceData>('/api/reference'),
    staleTime: 1000 * 60 * 30,
    select: (data): ReferenceData => {
      const others = data.users.filter((u) => u.id !== UNASSIGNED_USER_ID);
      const unassigned = data.users.find((u) => u.id === UNASSIGNED_USER_ID);
      // Ordem alfabética pt-BR (Item 7, lote 3) — defensivo sobre o ORDER BY do banco, que depende
      // do collation. Aplicado às 5 taxonomias; NÃO a leadUsers (o "TBD" fica ancorado no topo).
      // Exceção (emenda do lote 4): "Outros" sempre por último; no-op em listas sem "Outros".
      const byName = (a: ReferenceLookup, b: ReferenceLookup) => {
        const ao = a.name === 'Outros' ? 1 : 0;
        const bo = b.name === 'Outros' ? 1 : 0;
        if (ao !== bo) return ao - bo;
        return a.name.localeCompare(b.name, 'pt-BR');
      };
      return {
        ...data,
        discardReasons: [...data.discardReasons].sort(byName),
        theses: [...data.theses].sort(byName),
        sectors: [...data.sectors].sort(byName),
        funds: [...data.funds].sort(byName),
        originatorTypes: [...data.originatorTypes].sort(byName),
        investorTypes: [...data.investorTypes].sort(byName),
        coinvestmentTypes: [...data.coinvestmentTypes].sort(byName),
        decisionStructures: [...data.decisionStructures].sort(byName),
        users: others,
        leadUsers: unassigned ? [{ ...unassigned, name: TBD_LEAD_LABEL }, ...others] : others,
      };
    },
  });
}
